import { pipelineCapacityDisplay } from './pipeline-capacity';
import type { ParcelDetail, NearbyPipeline, AmlOverlap } from './api';
import { isHighRiskFloodZone, formatMW, formatAquiferName } from './score-helpers';

export const DESERT_SW_RE = /desert\s*southwest|tw\s*pipeline/i;
export const isDesertSW = (name: string | null | undefined) => DESERT_SW_RE.test(name || '');

// Seller-committed earthwork / site prep flag, derived from free-text site_notes.
// Single source of truth for the "landowner will grade" mitigation badge shown
// on V1 Physical Site, V2 thesis prose, and V2 kill-criteria rows. If the note
// phrasing ever changes, update it here and every surface picks up the fix at once.
export function hasLandownerEarthwork(parcel: { site_notes?: string | null }): boolean {
  return !!(parcel.site_notes && /LAND READINESS|earthwork/i.test(parcel.site_notes));
}

// Extract the LAND READINESS paragraph from site_notes (used by the V2 kill-criteria
// row to show the seller's exact commitment verbatim). Returns null if absent.
export function extractLandReadiness(notes: string | null | undefined): string | null {
  if (!notes) return null;
  const match = notes.match(/LAND READINESS[\s\S]*?(?:\n\s*\n|$)/i);
  if (!match) return null;
  const body = match[0].replace(/^\s*LAND READINESS\s*\n?/i, '').trim();
  return body || null;
}

export type ConfidenceLevel = 'confirmed' | 'preliminary' | 'assumed' | 'pending';
export interface ConfidenceItem { field: string; value: string; confidence: ConfidenceLevel; source: string }

export const CONFIDENCE_COLORS: Record<ConfidenceLevel, string> = {
  confirmed: '#5C6B45',
  preliminary: '#506272',
  assumed: '#9A7B30',
  pending: '#788894',
};

export const CONFIDENCE_LABELS: Record<ConfidenceLevel, string> = {
  confirmed: 'WHAT WE KNOW',
  preliminary: 'WHAT WE BELIEVE',
  assumed: 'WORKING ASSUMPTIONS',
  pending: 'REQUIRES CONFIRMATION',
};

// The one carve-out whose featured pipeline is the row the SERVER pinned rather than the
// highest-capacity row in the list. Meiners Ranch (La Salle): the server pins Camino Real
// (12.75″, 268.6 MW modeled) via applyHeadlineMWOverride, but the generic rule below returns
// the 42″ Gulf Coast Express long-haul header — so the hero, the Fuel Delivery card and the
// permit pathway headlined a 5.5 GW screening ceiling on contracted long-haul capacity, the
// exact claim the seed narrative disclaims ("a delivery point is a negotiation, not
// headroom"), while the Gas Supply section (which reads pipelines[0]) said Camino Real /
// 269 MW on the same page. Cooper Draw has the same shape: Enterprise's 30″ transmission
// crosses the ranch while the Whistler/Blackcomb/GCX 42″ header lines sit 13 mi from the rep
// centroid. Gated to these slugs so every other briefing — the protected client links
// included — keeps the generic rule byte-for-byte.
export const PINNED_HEADLINE_CARVE_OUT_SLUGS = new Set(['meiners-ranch-la-salle', 'cooper-draw-ranch']);

/**
 * Find the pipeline the briefing narrative features — the one we present as "the interconnect candidate."
 * Priority: Desert SW (corridor thesis), then the server-pinned headline for
 * PINNED_HEADLINE_CARVE_OUT_SLUGS, then best-by-MW match, then nearest.
 * This must be used everywhere the briefing attributes specific pipeline properties
 * (diameter, MAOP, distance, capacity) so the numbers are internally consistent.
 */
export function findFeaturedPipeline(
  _parcel: ParcelDetail,
  pipelines: NearbyPipeline[] | null,
  carveOutSlug?: string | null,
): NearbyPipeline | null {
  if (!pipelines || pipelines.length === 0) return null;
  // Skip operator-commitment pseudo-rows (e.g. Revenant operator gathering)
  // when picking the briefing's narrative-featured pipeline. The pseudo-row
  // is a build-to-site commitment, not a current public-midstream line —
  // downstream consumers (DD checklist Pressure & Volume, Supply Timeline,
  // What This Means prose) expect a real geometry signal.
  const realPipelines = pipelines.filter(p => p.id !== 'revenant-operator-gathering');
  if (realPipelines.length === 0) return null;
  // Desert SW is the corridor thesis — always leads when present
  const dsw = realPipelines.find(p => isDesertSW(p.name));
  if (dsw) return dsw;
  // Curated carve-out: honor the row the server pinned (applyHeadlineMWOverride stamps
  // headline_override on the parcel's pipeline_available_name match). Falls through to the
  // generic rules when the pinned row is absent from this list — e.g. suppressed from the
  // table — so the function never returns null where it used to return a row.
  if (carveOutSlug && PINNED_HEADLINE_CARVE_OUT_SLUGS.has(carveOutSlug)) {
    const pinned = realPipelines.find(p => p.headline_override);
    if (pinned) return pinned;
  }
  // Published native ratings are source context; the selected line remains a physical candidate.
  const withCap = realPipelines.filter(p => (pipelineCapacityDisplay(p).capacityMmcf ?? 0) > 0);
  if (withCap.length > 0) {
    return withCap.reduce((best, p) =>
      (pipelineCapacityDisplay(p).capacityMmcf ?? 0) > (pipelineCapacityDisplay(best).capacityMmcf ?? 0) ? p : best
    );
  }
  // Fallback: physically nearest
  return realPipelines[0];
}

// Geometry-based "on site" cutoff: any pipeline within ~264 ft of the parcel.
// Matches the V2 threshold; used everywhere V1 and V2 format pipeline distance.
export const ON_SITE_THRESHOLD = 0.05;

// True when the parcel owner is the same entity as the pipeline operator/name.
// Requires ≥2 DISTINCT owner tokens (4+ chars each, after stripping common industry
// suffixes) to appear in the pipeline name/operator string. A single-token match
// ("Dominion Holdings" vs "Dominion Transmission", "Permian Resources" vs
// "Permian Highway") is ambiguous — could be the same entity, could be an
// industry-common word. Two-token overlap is a strong identity signal (Goff:
// "goff" + "connector" both appear in "Goff Connector Pipeline").
export function ownerOwnsPipeline(
  ownerName: string | null | undefined,
  pipeline: { name: string | null; operator: string | null } | null | undefined,
): boolean {
  if (!ownerName || !pipeline) return false;
  const normalize = (x: string) => x.toLowerCase()
    .replace(/\b(llc|lp|inc|ltd|co|corp|company|holdings|energy|gas|pipeline|operating|usa|us)\b/g, ' ')
    .replace(/[^a-z ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const ownerWords = normalize(ownerName).split(' ').filter((w) => w.length >= 4);
  const pipeStr = [pipeline.name, pipeline.operator].filter(Boolean).map((x) => normalize(x as string)).join(' ');
  const matchCount = new Set(ownerWords.filter((w) => pipeStr.includes(w))).size;
  return matchCount >= 2;
}

// A pipeline is "effectively on site" when any of these hold:
//   1. Geometry distance ≤ ON_SITE_THRESHOLD (~264 ft — truly adjacent or crossing).
//   2. Parcel owner is the same entity as the pipeline operator (tap is guaranteed).
//   3. A WV DEP permit override pins this pipeline as the authoritative capacity source
//      for this parcel (permit names override nearest-segment geometry for BTM supply).
// Curated opt-out (structured_intel.suppress_lateral_estimates, Meiners Ranch): the
// briefing still says a lateral is required but stops pricing and scheduling it — the
// industry $/mile and month ranges are not derived from anything about this site, and
// Matthew's rule for Integra copy is facts over estimates. Absent the key (every other
// briefing) the ranges render exactly as before.
export function lateralEstimatesSuppressed(parcel: ParcelDetail): boolean {
  return (parcel as any)?.structured_intel?.suppress_lateral_estimates === true;
}

export function pipelineIsEffectivelyOnSite(
  parcel: ParcelDetail,
  pipeline: NearbyPipeline | null | undefined,
): boolean {
  if (!pipeline) return false;
  if (pipeline.distance_miles != null && Number(pipeline.distance_miles) <= ON_SITE_THRESHOLD) return true;
  if (ownerOwnsPipeline(parcel.owner_name, pipeline)) return true;
  const src = parcel.pipeline_available_local_source;
  if (src && src.startsWith('wvdep_permit')
      && parcel.pipeline_available_name
      && pipeline.name === parcel.pipeline_available_name) {
    return true;
  }
  return false;
}

export function computeGateStatus(parcel: ParcelDetail, pipelines: NearbyPipeline[] | null) {
  // Floodway gate: trip only on SEVERE flood exposure (≥30% of the parcel in
  // 100-yr Zone A). The historical binary check tripped on any zone A touch,
  // which knocked out parcels with sub-10% drainage corridors that are still
  // 90%+ buildable. The 30% threshold matches BriefingContent.tsx's
  // floodSeverity() severe level. Fall back to the binary check when pct
  // hasn't been computed yet (counties outside the FEMA-NFHL ingest scope).
  const floodway = parcel.flood_zone_pct_100yr != null
    ? Number(parcel.flood_zone_pct_100yr) >= 30
    : !!(parcel.flood_zone && isHighRiskFloodZone(parcel.flood_zone));

  const gates = {
    nonattainment: parcel.in_nonattainment === true,
    floodway,
    government_owner: !!parcel.is_government_owner,
    no_gas_path: !pipelines || pipelines.length === 0,
    too_small: parcel.acreage != null && Number(parcel.acreage) < 40,
    ej_concern: parcel.ej_max_pctile != null && parcel.ej_max_pctile >= 90,
  };
  const hardGatePass = !gates.nonattainment && !gates.floodway && !gates.government_owner && !gates.no_gas_path && !gates.too_small && !gates.ej_concern;
  return { ...gates, hardGatePass };
}

export interface PermitPrecedent {
  name: string;
  location: string;
  mw: number;
  timeline: string;
  detail: string;
}

export interface PermitPhase {
  label: string;       // "Phases 1 – 2"
  mwRange: string;     // "160 – 320 MW"
  pathway: string;     // "TCEQ Standard Permit"
  detail: string;      // ~98 TPY NOx per phase...
  timeline: string;    // "1–3 mo"
  color: 'green' | 'amber';
}

export interface PermitLadder {
  trancheMw: number;
  noxTpy: number;          // NOx TPY per phase (e.g., 98)
  noxPct: number;          // % of 250 TPY threshold (e.g., 39)
  phases: PermitPhase[];
  thresholdLabel: string;  // "Minor source ceiling — 250 TPY NOx (~407 MW)"
  nextSteps: string[];     // parallel-track + pre-application bullets
  coWarning: string;       // CO callout text
  scopeLine: string;
}

export interface PermitPathway {
  pathway: string;
  timeline: string;
  bullets: string[];
  riskLevel: 'low' | 'medium' | 'high';
  agency?: string;
  majorSourceThreshold?: string;
  offsetRatio?: string;
  emissionsProfile?: EmissionsProfile;
  precedents?: PermitPrecedent[];
  ladder?: PermitLadder;
}

// Ozone classification → major source NOx threshold and offset ratio
const OZONE_CLASSIFICATION: Record<string, { threshold_tpy: number; offset_ratio: string }> = {
  'Marginal': { threshold_tpy: 100, offset_ratio: '1.1:1' },
  'Moderate': { threshold_tpy: 100, offset_ratio: '1.15:1' },
  'Serious': { threshold_tpy: 50, offset_ratio: '1.2:1' },
  'Severe': { threshold_tpy: 25, offset_ratio: '1.3:1' },
  'Severe 15': { threshold_tpy: 25, offset_ratio: '1.3:1' },
  'Severe 17': { threshold_tpy: 25, offset_ratio: '1.3:1' },
  'Extreme': { threshold_tpy: 10, offset_ratio: '1.5:1' },
};

// BTM emission factors — calibrated from TCEQ permits (last reviewed 2026-04-14):
// Sandow Lakes 1.2 GW (Lee Co.), GW Ranch 7.65 GW (Pecos Co.), Fermi Matador 6 GW (Amarillo).
// DD checklist items (Emissions per tranche, Carbon footprint) rely on these as ADDRESSED screening estimates.
// Recalibrate when: new TCEQ permit data lands, EPA NSPS rules change, or PIA MAOP response requires prime-mover assumption update.
export const BTM_CALIBRATION = {
  GHG_TPY_PER_MW: 4150,              // tCO2e/yr per MW (midpoint: GW Ranch 4,314 + Matador 4,000)
  REGULATED_TPY_PER_MW: 1.57,        // total regulated pollutants TPY/MW (GW Ranch: 12,000/7,650)
  NOX_TPY_PER_MW_UNCONTROLLED: 11.9, // simple-cycle aeroderivative, no controls
  NOX_TPY_PER_MW_SCR: 1.2,           // with SCR (90% NOx reduction)
  GAS_MMCF_PER_MW: 1 / 5.07,         // ~0.197 MMcf/day per MW
  CONTESTED_CASE_MW: 500,             // community opposition threshold (Sandow Lakes precedent)
  TRANCHE_MW: 160,                    // Integra standard phase size (recip engine block)
  NOX_TPY_PER_MW_SP: 0.61,           // TCEQ EGU Standard Permit, 2007-amended (0.14 lb/MWh, East TX units >300 hr/yr)
  MINOR_SOURCE_CEILING_MW: 407,      // ~250 TPY / 0.61 TPY/MW — max MW before NOx triggers PSD
};

export interface EmissionsProfile {
  mw: number;
  ghg_tpy: number;
  regulated_tpy: number;
  gas_mmcf_day: number;
  gas_bcf_day: number | null;
  exceeds_ghg_reporting: boolean;
  major_source_status: 'below' | 'uncontrolled-only' | 'with-scr';
  major_source_mw_uncontrolled: number;
  major_source_mw_scr: number;
  contested_case_risk: boolean;
  scale_label: 'minor' | 'major' | 'gw-scale';
  gas_vs_capacity: 'within' | 'exceeds' | 'unknown';
}

export function computeEmissionsProfile(
  mw: number,
  noxThresholdTpy: number,
  pipelineCapacityMmcf?: number,
): EmissionsProfile | null {
  if (!mw || mw < 1) return null;

  const ghg = Math.round(mw * BTM_CALIBRATION.GHG_TPY_PER_MW);
  const regulated = Math.round(mw * BTM_CALIBRATION.REGULATED_TPY_PER_MW);
  const gasMmcf = +(mw * BTM_CALIBRATION.GAS_MMCF_PER_MW).toFixed(1);
  const gasBcf = gasMmcf >= 100 ? +(gasMmcf / 1000).toFixed(2) : null;

  const mwUncontrolled = Math.round(noxThresholdTpy / BTM_CALIBRATION.NOX_TPY_PER_MW_UNCONTROLLED);
  const mwScr = Math.round(noxThresholdTpy / BTM_CALIBRATION.NOX_TPY_PER_MW_SCR);

  let majorStatus: EmissionsProfile['major_source_status'] = 'below';
  if (mw >= mwScr) majorStatus = 'with-scr';
  else if (mw >= mwUncontrolled) majorStatus = 'uncontrolled-only';

  let scale: EmissionsProfile['scale_label'] = 'minor';
  if (mw >= 1000) scale = 'gw-scale';
  else if (mw >= mwUncontrolled) scale = 'major';

  let gasVsCapacity: EmissionsProfile['gas_vs_capacity'] = 'unknown';
  if (pipelineCapacityMmcf != null && pipelineCapacityMmcf > 0) {
    gasVsCapacity = gasMmcf <= pipelineCapacityMmcf * 0.9 ? 'within' : 'exceeds';
  }

  return {
    mw,
    ghg_tpy: ghg,
    regulated_tpy: regulated,
    gas_mmcf_day: gasMmcf,
    gas_bcf_day: gasBcf,
    exceeds_ghg_reporting: ghg > 25000,
    major_source_status: majorStatus,
    major_source_mw_uncontrolled: mwUncontrolled,
    major_source_mw_scr: mwScr,
    contested_case_risk: mw >= BTM_CALIBRATION.CONTESTED_CASE_MW,
    scale_label: scale,
    gas_vs_capacity: gasVsCapacity,
  };
}

function getNnsrDetails(nonattainmentAreas?: Array<{ pollutant: string; classification?: string }>): { classification: string | null; threshold: number; offsetRatio: string } {
  if (!nonattainmentAreas?.length) return { classification: null, threshold: 100, offsetRatio: '1.1:1' };
  // Find worst ozone classification (lowest threshold)
  let worstThreshold = 100;
  let worstRatio = '1.1:1';
  let worstClass: string | null = null;
  for (const na of nonattainmentAreas) {
    const cls = na.classification;
    if (cls && OZONE_CLASSIFICATION[cls]) {
      const info = OZONE_CLASSIFICATION[cls];
      if (info.threshold_tpy < worstThreshold) {
        worstThreshold = info.threshold_tpy;
        worstRatio = info.offset_ratio;
        worstClass = cls;
      }
    }
  }
  return { classification: worstClass, threshold: worstThreshold, offsetRatio: worstRatio };
}

export function computePermitPathway(
  parcel: ParcelDetail,
  pipelineCapacityMmcf?: number,
  // Corrected TX permitting content (2026-07-22 primary-source review, A1-gated at
  // first; generalized to every TX-attainment briefing 2026-08-26 per the external
  // audit + Matthew's approval). The retired generic ladder cited the separate
  // six-engine/400-hr Jan-2025 NG-EGU standard permit; the instrument the precedents
  // actually used is the 2007-amended EGU Standard Permit.
  opts?: { crownpineA1?: boolean },
): PermitPathway {
  const result = computePermitPathwayInner(parcel);
  void opts;

  // TX attainment: replace the generic result's headline fields (its "9–45 days"
  // timeline, "with SCR" framing, and the stale "SP 6005 (Jan 2025)" bullet). One
  // consistent story: 2007-amended EGU Standard Permit, ~3–6 mo planning envelope.
  if (parcel.state === 'TX' && parcel.in_nonattainment === false) {
    result.pathway = 'TCEQ EGU Standard Permit';
    result.timeline = '~3–6 mo incl. pre-application (TCEQ processing of complete filings has run in weeks)';
    result.bullets = [
      `160 MW phase at the 0.14 lb/MWh EGU Standard Permit limit → ~${Math.round(160 * BTM_CALIBRATION.NOX_TPY_PER_MW_SP)} TPY NOx — 39% of the 250 TPY major-source threshold`,
      'Instrument: EGU Standard Permit (2007-amended, non-rule) — registration-based; the instrument behind Longhorn/Crusoe (~360 MW baseload), Stargate (709 MW), and Misae (519 MW)',
      'Controls equipment-specific: the permit does not prescribe SCR, but selected equipment must meet its limits — Longhorn and Misae used SCR; Misae added oxidation catalysts',
      'Title V may be required even where PSD is not triggered (Longhorn, Stargate, and Misae all took it)',
      'The separate Jan 30, 2025 natural-gas EGU standard permit (six engines, 400 hr/yr) is a limited-use instrument — NOT this pathway',
      'Confirm current applicability, source aggregation, and per-pollutant controlled PTE with TCEQ at pre-application',
      'Application: Form PI-1S via STEERS',
    ];
    // 'medium' matches the client-facing risk register (permitting = MED):
    // Phase 1 is a strong SP candidate but Phase 2 stays equipment/PTE-dependent.
    result.riskLevel = 'medium';
  }

  const state = parcel.state || '';
  const trancheMw = BTM_CALIBRATION.TRANCHE_MW;

  // Determine NOx threshold based on nonattainment classification
  const noxThreshold = parcel.in_nonattainment
    ? getNnsrDetails(parcel.nonattainment_areas).threshold
    : 250;

  // Compute emissions profile at Integra's 160 MW phase size (not full pipeline capacity)
  const profile = computeEmissionsProfile(trancheMw, noxThreshold, pipelineCapacityMmcf);
  result.emissionsProfile = profile ?? undefined;

  // --- Quantitative bullets from emissions profile ---
  if (profile) {
    // GHG estimate (replaces the old vague "likely exceeds" bullet)
    if (profile.exceeds_ghg_reporting) {
      result.bullets.push(`GHG estimate: ~${profile.ghg_tpy.toLocaleString()} tCO2e/yr at ${Math.round(profile.mw).toLocaleString()} MW (reporting threshold: 25,000 tCO2e/yr per 40 CFR Part 98)`);
    }

    // Gas consumption
    if (profile.mw >= 50) {
      const gasStr = profile.gas_bcf_day
        ? `${profile.gas_bcf_day} Bcf/day (${profile.gas_mmcf_day.toLocaleString()} MMcf/day)`
        : `${profile.gas_mmcf_day.toLocaleString()} MMcf/day`;
      result.bullets.push(`Gas requirement at ${Math.round(profile.mw).toLocaleString()} MW: ~${gasStr}`);
    }

    // Major source threshold context (nonattainment-aware)
    if (profile.major_source_status === 'uncontrolled-only') {
      if (parcel.in_nonattainment) {
        result.bullets.push(`At ${Math.round(profile.mw).toLocaleString()} MW in nonattainment area, facility exceeds ${noxThreshold} TPY NOx threshold without controls. SCR (90% reduction) required to stay below major source at ~${profile.major_source_mw_scr} MW`);
      } else if (state === 'TX') {
        result.bullets.push(`At ${Math.round(profile.mw).toLocaleString()} MW the selected equipment must meet the EGU Standard Permit limits (0.14 lb/MWh NOx for units >300 hr/yr) and keep facility-wide controlled PTE below every 250 TPY major-source threshold. Controls are equipment-specific — Longhorn (Crusoe) and Misae both used SCR; Misae added oxidation catalysts. Title V may apply even where PSD does not`);
      } else {
        result.bullets.push(`At ${Math.round(profile.mw).toLocaleString()} MW, SCR controls (90% NOx reduction) required to avoid Major Source PSD review`);
      }
    } else if (profile.major_source_status === 'with-scr') {
      result.bullets.push(`At ${Math.round(profile.mw).toLocaleString()} MW, facility exceeds ${noxThreshold} TPY NOx even with SCR controls (threshold: ~${profile.major_source_mw_scr} MW). PSD/BACT required regardless of control technology`);
    }

    // Contested case risk (TX GW-scale)
    if (profile.contested_case_risk && state === 'TX') {
      result.bullets.push('Contested case risk: organized opposition can add 9\u201318 months via SOAH litigation. Sandow Lakes (1.2 GW) was contested; GW Ranch (7.65 GW) and Fermi Matador (6 GW) avoided it through remote siting');
    }
  } else if (trancheMw >= 50) {
    // Fallback: no profile but significant MW — keep a simple GHG bullet
    result.bullets.push(`GHG reporting: facility at ~${Math.round(trancheMw).toLocaleString()} MW likely exceeds 25,000 tCO2e/yr threshold (40 CFR Part 98)`);
  }

  // EJ + nonattainment compound risk
  if (trancheMw >= 25 && parcel.in_nonattainment === true) {
    const ejPct = parcel.ej_max_pctile;
    if (ejPct != null && ejPct >= 80) {
      result.bullets.push('Public comment required + EJ community - elevated community opposition risk');
    }
  }

  // --- TX BTM Precedents (scale-appropriate, verified from TCEQ dockets) ---
  if (state === 'TX' && profile) {
    // Corrected precedent set (TCEQ technical reviews pulled 2026-07-22). All standard-
    // permit projects below registered under the 2007-amended EGU Standard Permit —
    // NOT the separate Jan 30, 2025 NG-EGU standard permit (6-engine/400-hr, limited use).
    result.precedents = [
      {
        name: 'Crusoe / Longhorn Data Center (Taylor County)',
        location: 'Abilene, TX — 10 turbines, ~360 MW — Reg #177263',
        mw: 360,
        timeline: 'Standard Permit',
        detail: 'The flagship US BTM data-center campus, permitted baseload (8,760 hr/yr represented) under the 2007-amended EGU Standard Permit; TCEQ answered "No" to PSD applicability. Turbines are SCR-equipped; Title V coverage obtained. The Jan 2025 modification of the existing registration was reviewed in 11 days after a complete filing, a processing benchmark for modifications, not an original-permit timeline. A case-by-case PSD expansion application (submitted Nov 7, 2025) is pending for the next phase.',
      },
      {
        name: 'VoltaGrid / Oracle Stargate (Shackelford County)',
        location: '210 engines, 709 MW (197 operating simultaneously) — Reg #180842, approved Jul 17, 2025',
        mw: 709,
        timeline: '3-day TCEQ processing (complete filing)',
        detail: 'Largest single standard-permit registration identified; 100% behind-the-meter microgrid for the Oracle data-center campus. Registered under the ≤10 MW-per-unit West Texas limit (3.11 lb/MWh — less stringent than the 0.14 lb/MWh applicable to this site), at 143 TPY NOx / 229 TPY CO represented; Title V required. Capacity alone does not establish eligibility — the emissions engineering does.',
      },
      {
        name: 'Misae Gas Power (San Antonio area)',
        location: '206 engines, 518.7 MW — received Dec 18, 2025; signed Jan 14, 2026',
        mw: 519,
        timeline: '~27 days',
        detail: 'Emissions engineered below 250 TPY using SCR plus oxidation catalysts at 8,760 hr/yr; Title V required (NOx, CO, and PM each above 100 TPY). Confirms the 2007 EGU Standard Permit path in active use through January 2026.',
      },
      {
        name: 'Sandow Lakes Energy (Lee County)',
        location: 'Lee County, TX — 1,200 MW CCGT — Permit Nos. 177380, PSDTX1650',
        mw: 1200,
        timeline: '~14 months (PSD)',
        detail: 'Contested case hearing requested by "Move the Gas Plant" coalition — denied by TCEQ Oct 2025 in 90-second vote. Demonstrates PSD timeline even with organized opposition.',
      },
      {
        name: 'Project Matador / Fermi America (Carson County)',
        location: 'Carson County, TX — 6,000 MW — Permit Nos. 181009, PSDTX1670',
        mw: 6000,
        timeline: '~7 months (Expedited PSD)',
        detail: 'Filed Aug 2025, approved Feb 2026. Used $20K Subchapter J expedited surcharge + 90 identical Siemens SGT-800 turbines to simplify TCEQ review.',
      },
    ];
  }

  // --- Phased Permitting Ladder (TX attainment, corrected 2007-amended EGU SP version) ---
  if (state === 'TX' && parcel.in_nonattainment === false && profile) {
    const noxPerPhase = Math.round(BTM_CALIBRATION.TRANCHE_MW * BTM_CALIBRATION.NOX_TPY_PER_MW_SP); // ~98 TPY at 0.14 lb/MWh
    const noxPctOfThreshold = Math.round((noxPerPhase / 250) * 100);

    result.ladder = {
      trancheMw: BTM_CALIBRATION.TRANCHE_MW,
      noxTpy: noxPerPhase,
      noxPct: noxPctOfThreshold,
      phases: [
        {
          label: 'Phase 1',
          mwRange: `${BTM_CALIBRATION.TRANCHE_MW} MW`,
          pathway: 'TCEQ EGU Standard Permit (2007-amended)',
          detail: `~${noxPerPhase} TPY NOx at the 0.14 lb/MWh limit — ${noxPctOfThreshold}% of the 250 TPY major-source threshold. Registration-based; no case-by-case review. Controls are equipment-specific: the permit does not prescribe SCR, but the selected equipment must meet its limits — Longhorn and Misae both used SCR (Misae added oxidation catalysts). Title V may apply even where PSD does not.`,
          timeline: '~3–6 mo incl. pre-app',
          color: 'green',
        },
        {
          label: 'Phase 2',
          mwRange: `${BTM_CALIBRATION.TRANCHE_MW * 2} MW cumul.`,
          pathway: 'Standard Permit — conditional',
          detail: `~${noxPerPhase * 2} TPY NOx stays under 250, but CO is the watch item: at the VoltaGrid SAT-1 factor (~0.81 TPY/MW), ${BTM_CALIBRATION.TRANCHE_MW * 2} MW ≈ 259 TPY CO — over the threshold unless the selected engine + oxidation-catalyst package holds enforceable CO lower. Eligibility depends on engine-specific controlled PTE, source aggregation, and every regulated NSR pollutant.`,
          timeline: 'engine-specific',
          color: 'amber',
        },
        {
          label: 'Beyond',
          mwRange: `>${BTM_CALIBRATION.TRANCHE_MW * 2} MW cumul.`,
          pathway: 'Case-by-case: PSD if PTE crosses 250 TPY',
          detail: 'Cumulative controlled PTE above 250 TPY for any NSR pollutant triggers PSD: BACT + AERMOD + public notice. No automatic MW trigger: Misae registered 518.7 MW under the Standard Permit; the line is aggregated controlled PTE, not nameplate. Well-established process (Sandow ~14 mo, Matador ~7 mo expedited). Crusoe filed exactly this expansion for Longhorn in Nov 2025.',
          timeline: '18–24 mo',
          color: 'amber',
        },
      ],
      thresholdLabel: `Major-source line — 250 TPY per NSR pollutant (NOx reaches it near ~${BTM_CALIBRATION.MINOR_SOURCE_CEILING_MW} MW at the SP limit; CO likely binds earlier)`,
      nextSteps: [
        `Parallel-track strategy: File the Phase 1 standard-permit registration; begin PSD pre-application (BACT, dispersion modeling) during Phase 1 construction so Phase 1–2 revenue precedes the PSD tranche. TCEQ processing of complete filings has run in weeks (Longhorn modification 11 days; Misae ~27 days) — plan ~3–6 months including engineering and pre-application coordination.`,
        `Pre-application with TCEQ: (1) Confirm current applicability of the 2007-amended EGU Standard Permit, in active use through January 2026 (Misae) and currently listed by TCEQ; the separate Jan 30, 2025 natural-gas EGU standard permit (six engines, 400 hr/yr) is a limited-use instrument and NOT this pathway. (2) Model CO with the selected engine platform (VoltaGrid SAT-1 benchmark ~0.81 TPY/MW). (3) Resolve the source boundary early under EPA's three-part test (common control, contiguous/adjacent property, same SIC major group); separate ownership alone does not resolve aggregation. (4) Verify HAPs: formaldehyde thresholds (10 TPY individual / 25 TPY aggregate).`,
      ],
      coWarning: `CO is likely the binding pollutant before NOx. Reference: VoltaGrid SAT-1 was authorized at 215 TPY CO at 265 MW (~0.81 TPY/MW, represented maximums, not measured actuals); at that factor ${BTM_CALIBRATION.TRANCHE_MW * 2} MW ≈ 259 TPY, above the 250 TPY threshold. Enforceable CO rates are engine- and catalyst-specific; confirm with engine-specific modeling during pre-application.`,
      scopeLine: 'Air permitting is one component of site regulatory analysis. ERCOT interconnection (if any), pipeline laterals, water, and tax structuring also require assessment. Permit facts verified against TCEQ records July 2026.',
    };
  }

  // --- Phased Permitting Ladder (WV attainment) ---
  if (state === 'WV' && parcel.in_nonattainment === false && profile) {
    const noxPerPhaseScr = Math.round(BTM_CALIBRATION.TRANCHE_MW * BTM_CALIBRATION.NOX_TPY_PER_MW_SCR);
    const noxPctOfThreshold = Math.round((noxPerPhaseScr / 250) * 100);
    const ceilingMw = Math.floor(250 / BTM_CALIBRATION.NOX_TPY_PER_MW_SCR);

    result.ladder = {
      trancheMw: BTM_CALIBRATION.TRANCHE_MW,
      noxTpy: noxPerPhaseScr,
      noxPct: noxPctOfThreshold,
      phases: [
        {
          label: 'Phase 1',
          mwRange: `${BTM_CALIBRATION.TRANCHE_MW} MW`,
          pathway: 'WV DEP Minor Source (45CSR13)',
          detail: `~${noxPerPhaseScr} TPY NOx with SCR \u2014 ${noxPctOfThreshold}% of 250 TPY threshold. No dispersion modeling. No BACT. ~7 month processing. HB 2014 microgrid certification runs in parallel.`,
          timeline: '6\u20139 mo',
          color: 'green',
        },
        {
          label: 'Phase 2+',
          mwRange: `${BTM_CALIBRATION.TRANCHE_MW * 2}+ MW cumul.`,
          pathway: 'WV DEP PSD (45CSR14)',
          detail: `Cumulative NOx exceeds 250 TPY (~${noxPerPhaseScr * 2} TPY at ${BTM_CALIBRATION.TRANCHE_MW * 2} MW). PSD with BACT required. AERMOD dispersion modeling + ambient air quality analysis + 30-day public comment. Statutory max 365 days after completeness.`,
          timeline: '12\u201318 mo',
          color: 'amber',
        },
      ],
      thresholdLabel: `Minor source ceiling \u2014 250 TPY NOx with SCR (~${ceilingMw} MW)`,
      nextSteps: [
        `Parallel-track strategy: File Phase 1 minor source permit (45CSR13) and HB 2014 "High Impact" certification simultaneously. Begin PSD pre-application (BACT analysis, AERMOD modeling) during Phase 1 construction. Phase 1 generating revenue before Phase 2 PSD approval is needed \u2014 compresses total buildout by 12+ months.`,
        `Anti-piecemealing risk: WV DEP and EPA enforce source aggregation rules. If the master plan calls for 1 GW on a single contiguous site under common control, DEP may require permitting the full Potential to Emit (PTE) upfront \u2014 ~1,200 TPY NOx at 1 GW, deep into PSD territory. Mitigation: (1) HB 2014 microgrid district framework may support phased permitting as a certified microgrid \u2014 Monarch/Nscale (2 GW) is proceeding under this framework. (2) Structure each phase as a separate operating entity to limit aggregation exposure. (3) Engage WV DEP-DAQ pre-application to establish phased permitting protocol before filing.`,
        `Pre-application with WV DEP-DAQ: (1) Confirm RICE source category \u2014 reciprocating engines are not \u201Cfossil fuel-fired steam electric,\u201D supporting 250 TPY over 100 TPY. (2) Model CO emissions \u2014 CO rates vary by engine model; VoltaGrid benchmark is ~0.81 TPY/MW but some RICE configurations run higher. CO may trigger PSD before NOx at scale. (3) Verify HAPs \u2014 formaldehyde thresholds (10 TPY individual / 25 TPY aggregate). (4) Confirm Title V (45CSR30) trigger timing.`,
      ],
      coWarning: 'CO may be the binding pollutant. Reference: VoltaGrid SAT-1 emitted 215 TPY CO at 265 MW (~0.81 TPY/MW). At 160 MW, that rate yields ~130 TPY CO \u2014 below the 250 TPY threshold but with less margin than NOx. CO emission rates vary significantly by engine model and load profile. Engine-specific modeling required during pre-application to confirm CO does not trigger PSD before NOx.',
      scopeLine: 'Air permitting is one component of site regulatory analysis. PJM interconnection (if any grid export), pipeline laterals, water withdrawal permits (NPDES), HB 2014 certification, and county zoning all require assessment. Current as of April 2026.',
    };
  }

  return result;
}

function computePermitPathwayInner(
  parcel: ParcelDetail,
): PermitPathway {
  const state = parcel.state || '';
  const county = parcel.county || '';
  // Per-phase MW determines permit pathway (Integra builds in 160 MW tranches)
  const mw = BTM_CALIBRATION.TRANCHE_MW;

  // --- NONATTAINMENT ---
  if (parcel.in_nonattainment === true) {
    const nnsr = getNnsrDetails(parcel.nonattainment_areas);
    const pollutants = parcel.nonattainment_areas?.map(na => na.pollutant).join(', ') || 'criteria pollutant';
    const nnsrBullets = [
      `Nonattainment pollutant(s): ${pollutants}`,
      'LAER (Lowest Achievable Emission Rate) technology controls required',
      'Emission offset credits required',
      'Mandatory public comment period',
    ];

    if (state === 'TX') {
      return {
        pathway: 'TCEQ NNSR',
        timeline: '12\u201318+ months',
        bullets: [...nnsrBullets, 'TCEQ target review: 285 days (contested permits can exceed 18 months)', 'Application: Form PI-1S via STEERS'],
        riskLevel: 'high',
        agency: 'Texas Commission on Environmental Quality (TCEQ)',
        majorSourceThreshold: nnsr.classification ? `${nnsr.threshold} tpy NOx (${nnsr.classification})` : '250 tpy NOx',
        offsetRatio: nnsr.offsetRatio,
      };
    }
    if (state === 'NM') {
      return {
        pathway: 'NMED NNSR',
        timeline: '18\u201336+ months',
        bullets: [...nnsrBullets, 'NM minor source threshold: 10 lbs/hr or 25 tpy criteria pollutants', 'NMED applying heightened scrutiny to DC applications (Project Jupiter, 2025)'],
        riskLevel: 'high',
        agency: 'New Mexico Environment Department (NMED)',
        majorSourceThreshold: '25 tpy criteria pollutants (NM threshold)',
        offsetRatio: nnsr.offsetRatio,
      };
    }
    if (state === 'AZ' && county === 'Maricopa') {
      return {
        pathway: 'MCAQD NNSR',
        timeline: '18\u201336 months',
        bullets: [...nnsrBullets, 'Maricopa County Air Quality Dept operates independently from ADEQ', 'Separate permitting handbook and procedures'],
        riskLevel: 'high',
        agency: 'Maricopa County Air Quality Department (MCAQD)',
        majorSourceThreshold: nnsr.classification ? `${nnsr.threshold} tpy NOx (${nnsr.classification})` : '100 tpy NOx',
        offsetRatio: nnsr.offsetRatio,
      };
    }
    if (state === 'AZ') {
      return {
        pathway: 'ADEQ Class I (NNSR)',
        timeline: '12\u201324 months',
        bullets: nnsrBullets,
        riskLevel: 'high',
        agency: 'Arizona Department of Environmental Quality (ADEQ)',
        majorSourceThreshold: nnsr.classification ? `${nnsr.threshold} tpy NOx (${nnsr.classification})` : '100 tpy NOx',
        offsetRatio: nnsr.offsetRatio,
      };
    }
    return {
      pathway: 'Nonattainment NSR (NNSR)',
      timeline: '12\u201318+ months',
      bullets: nnsrBullets,
      riskLevel: 'high',
      majorSourceThreshold: nnsr.classification ? `${nnsr.threshold} tpy NOx` : '250 tpy NOx',
      offsetRatio: nnsr.offsetRatio,
    };
  }

  // --- ATTAINMENT ---
  if (parcel.in_nonattainment === false) {
    if (state === 'TX') {
      // Permit pathway gates based on NOx emissions with SCR at tranche MW
      // 160 MW × 1.2 TPY/MW (SCR) = 192 TPY — below 250 TPY = standard permit
      const noxWithScr = mw * BTM_CALIBRATION.NOX_TPY_PER_MW_SCR;

      // Exceeds 250 TPY even with SCR → full PSD with BACT
      if (noxWithScr >= 250) {
        return {
          pathway: 'TCEQ PSD',
          timeline: '7\u201318+ months',
          bullets: [
            `${mw} MW phase \u2192 ~${Math.round(noxWithScr)} TPY NOx with SCR \u2014 exceeds 250 TPY major source threshold`,
            'Prevention of Significant Deterioration (PSD) permit required',
            'BACT required: SCR mandatory (NOx \u2264 2.0 ppmvd combined cycle, 4.0 ppmvd simple cycle), oxidation catalysts for CO/VOC',
            'AERMOD dispersion modeling required: $50K\u2013$150K engineering cost, 2\u20134 months pre-application',
            'Expedited permitting available: $20,000 surcharge under 30 TAC Chapter 101, Subchapter J',
            'Application: Form PI-1S via STEERS + ePermits',
          ],
          riskLevel: 'high',
          agency: 'Texas Commission on Environmental Quality (TCEQ)',
          majorSourceThreshold: '250 tpy NOx (attainment area)',
        };
      }
      // Below 250 TPY with SCR → Standard Permit fast lane
      return {
        pathway: 'TCEQ Standard Permit',
        timeline: '9\u201345 days',
        bullets: [
          `${mw} MW phase with SCR \u2192 ~${Math.round(noxWithScr)} TPY NOx \u2014 below 250 TPY major source threshold`,
          'Emission-rate limited pathway (no hardcoded MW cap)',
          'TCEQ avg processing: 9 days; 48% of NSR projects auto-completed same day via ePermits',
          'No AERMOD dispersion modeling required',
          'No public notice period required (unless public notice SP)',
          'Instrument: EGU Standard Permit (2007-amended) — registration-based; verify equipment-specific controlled PTE with TCEQ',
          'Backup generators: PBR 106.511 (emergency, 876 hr/yr)',
          'Application: Form PI-1S via STEERS',
        ],
        riskLevel: 'low',
        agency: 'Texas Commission on Environmental Quality (TCEQ)',
        majorSourceThreshold: '250 tpy NOx (attainment area)',
      };
    }
    if (state === 'NM') {
      return {
        pathway: 'NMED Minor Source Permit',
        timeline: '6\u201312 months',
        bullets: [
          'NM minor source threshold: 10 lbs/hr or 25 tpy criteria pollutants',
          'Lower thresholds than TX - more facilities trigger permitting',
          'NMED applying heightened scrutiny to DC applications (Project Jupiter, 2025)',
          'Project Jupiter applications returned as "incomplete" by NMED',
        ],
        riskLevel: 'medium',
        agency: 'New Mexico Environment Department (NMED)',
        majorSourceThreshold: '25 tpy criteria pollutants (NM threshold)',
      };
    }
    if (state === 'AZ' && county === 'Maricopa') {
      return {
        pathway: 'MCAQD Class II Permit',
        timeline: '6\u201312 months',
        bullets: [
          'Maricopa County Air Quality Dept has its own permitting handbook',
          'Operates independently from state ADEQ',
          'County has active nonattainment designations for other pollutants (ozone, PM-10)',
          'Verify current status for all criteria pollutants before application',
        ],
        riskLevel: 'medium',
        agency: 'Maricopa County Air Quality Department (MCAQD)',
        majorSourceThreshold: '100 tpy NOx',
      };
    }
    if (state === 'AZ') {
      return {
        pathway: 'ADEQ Class II Permit',
        timeline: '3\u20136 months',
        bullets: [
          'ADEQ Class II permit for minor sources',
          'Standard permitting pathway - no special requirements',
        ],
        riskLevel: 'low',
        agency: 'Arizona Department of Environmental Quality (ADEQ)',
        majorSourceThreshold: '100 tpy NOx',
      };
    }
    if (state === 'WV') {
      const noxWithScr = mw * BTM_CALIBRATION.NOX_TPY_PER_MW_SCR;
      // BTM data centers use RICE (reciprocating engines), not CCGTs.
      // RICE are NOT "fossil fuel-fired steam electric >250 MMBTU/hr" — generic 250 TPY applies.
      const majorThreshold = 250;
      const isPSD = noxWithScr >= majorThreshold;
      return {
        pathway: isPSD ? 'WV DEP PSD (45CSR14)' : 'WV DEP Minor Source (45CSR13)',
        timeline: isPSD ? '12\u201318 months' : '6\u20139 months',
        bullets: [
          `WV DEP Division of Air Quality \u2014 delegated PSD authority from EPA Region 3`,
          `${mw} MW phase with SCR \u2192 ~${Math.round(noxWithScr)} TPY NOx \u2014 ${isPSD ? 'exceeds' : 'below'} ${majorThreshold} TPY major source threshold`,
          isPSD
            ? 'PSD construction permit (45CSR14) with BACT required. AERMOD dispersion modeling + ambient air quality analysis. Statutory max 365 days after completeness + 30-day public comment.'
            : 'Minor source construction permit (45CSR13). No dispersion modeling required. ~7 month processing.',
          'WV in attainment for all 7 NAAQS pollutants',
          'Source category note: BTM reciprocating engines (RICE) are not "fossil fuel-fired steam electric" units. The generic 250 TPY major source threshold applies. Confirm source classification with WV DEP-DAQ during pre-application \u2014 if DEP classifies the facility as steam electric (>250 MMBTU/hr), the threshold drops to 100 TPY and PSD is triggered.',
          'HB 2014 / HB 4983 "High Impact Data Center" + "Certified Microgrid District" framework (2025\u20132026) fast-tracks BTM siting. BTM generators with <10% wholesale export are exempt from PSC Siting Certificate and full utility regulation. Apply through WV Dept of Commerce.',
          'Mandatory joint public hearing in host county (Commerce + DOT + DEP) required before final certification.',
          isPSD ? 'Title V operating permit (45CSR30) also required within 12 months of startup' : '',
          'Risk: third-party appeals can delay or re-open permits (precedent: Ohio Valley Jobs Alliance challenges in the 2010s). Permits expire if construction not commenced.',
        ].filter(Boolean),
        riskLevel: isPSD ? 'medium' : 'low',
        agency: 'West Virginia Department of Environmental Protection, Division of Air Quality (WV DEP-DAQ)',
        majorSourceThreshold: `${majorThreshold} tpy NOx (RICE \u2014 confirm source category with DEP)`,
        precedents: [
          {
            name: 'Monarch Compute Campus / Nscale (Mason County)',
            location: 'Point Pleasant, WV \u2014 1,350 MW AI compute, 2 GW BTM gas gen \u2014 under construction',
            mw: 1350,
            timeline: 'H1 2028 (Phase 1)',
            detail: '2,380 acres with Caterpillar gensets. Microsoft LOI + NVIDIA Vera Rubin NVL72. Nscale acquired AIPCorp (Fidelis sponsor) March 2026. Using HB 2014 microgrid framework. First generator deliveries late 2026.',
          },
          {
            name: 'Google Putnam County Campus',
            location: 'Buffalo, WV \u2014 multibillion-dollar High Impact Data Center \u2014 announced March 2026',
            mw: 0,
            timeline: 'Certification in progress',
            detail: 'First project certified under HB 2014 "High Impact" framework. Google purchased land. Tracks administrative velocity for Dept of Commerce certification process.',
          },
          {
            name: 'Penzance Eastern Panhandle (Berkeley County)',
            location: 'Falling Waters, WV \u2014 600 MW, $4B, 548 acres \u2014 announced Feb 2026',
            mw: 600,
            timeline: 'TBD',
            detail: 'Natural gas BTM, HB 2014 "high impact" certified. 1.9M sqft at full buildout. DEP-DAQ filing status unknown.',
          },
        ],
      };
    }
    // Generic attainment
    if (mw >= 100) {
      return {
        pathway: 'Case-by-Case NSR',
        timeline: '10\u201315 months',
        bullets: ['BACT determination required'],
        riskLevel: 'medium',
        majorSourceThreshold: '250 tpy NOx',
      };
    }
    return {
      pathway: 'Standard Permit (Minor Source)',
      timeline: '45\u201360 days',
      bullets: ['Standard permit pathway - no special requirements'],
      riskLevel: 'low',
      majorSourceThreshold: '250 tpy NOx',
    };
  }

  // --- UNKNOWN ---
  return {
    pathway: 'Permit Pathway TBD',
    timeline: 'TBD',
    bullets: ['Attainment status not yet confirmed for this area', 'Permit pathway cannot be determined until EPA data is available'],
    riskLevel: 'medium',
  };
}

export interface StructuredRisk {
  description: string;
  severity: 'HIGH' | 'MED' | 'LOW';
  mitigation: string;
}

export function computeStructuredRisks(
  parcel: ParcelDetail,
  pipelines: NearbyPipeline[] | null,
): StructuredRisk[] {
  const risks: StructuredRisk[] = [];
  const gates = computeGateStatus(parcel, pipelines);

  if (gates.nonattainment) risks.push({ description: 'EPA nonattainment area (NNSR permitting required)', severity: 'HIGH', mitigation: 'Verify NNSR pathway with state agency; evaluate emission offset availability and LAER technology requirements' });
  if (gates.floodway) risks.push({ description: `High-risk flood zone (FEMA ${parcel.flood_zone})`, severity: 'HIGH', mitigation: 'Commission FEMA NFHL review + site survey; may constrain pad placement' });
  if (gates.government_owner) risks.push({ description: 'Government/utility owner', severity: 'HIGH', mitigation: 'Explore surface lease or alternative parcels; government dispositions require extended timeline' });
  if (gates.no_gas_path) risks.push({ description: 'No viable gas delivery path within 10 miles', severity: 'HIGH', mitigation: 'Contact regional pipeline operators for lateral feasibility study' });
  if (gates.too_small) risks.push({ description: `Insufficient acreage (${parcel.acreage} ac)`, severity: 'HIGH', mitigation: 'Evaluate adjacent parcels for land assembly to reach 40+ acre minimum' });

  if (parcel.avg_slope_pct != null && Number(parcel.avg_slope_pct) >= 5) {
    const terrainMitigation = hasLandownerEarthwork(parcel)
      ? 'Landowner has committed to earthwork and site preparation for qualified buyers; geotech assessment still required to confirm grading scope and cost.'
      : 'Geotech assessment required; estimate grading cost before advancing';
    const slopeVal = Number(parcel.avg_slope_pct);
    const terrainLabel = slopeVal >= 15 ? 'Steep terrain' : slopeVal >= 10 ? 'Moderate grade' : 'Rolling terrain';
    risks.push({ description: `${terrainLabel} (${parcel.avg_slope_pct}% avg slope)`, severity: 'MED', mitigation: terrainMitigation });
  }
  if (parcel.avg_slope_pct == null)
    risks.push({ description: 'Terrain data unavailable — slope not assessed', severity: 'HIGH', mitigation: 'Commission geotechnical survey before advancing site evaluation' });
  if (parcel.colocated_owners && parcel.colocated_owners.length > 2)
    risks.push({ description: `Fractured ownership (${parcel.colocated_owners.length + 1} owners)`, severity: 'MED', mitigation: 'Title search and coordinated acquisition strategy' });
  if (parcel.fiber_available === false && (parcel.nearest_fiber_miles == null || Number(parcel.nearest_fiber_miles) > 10))
    risks.push({ description: 'No fiber connectivity', severity: 'MED', mitigation: 'Contact fiber carriers for LOI; evaluate microwave backhaul as interim' });
  else if (parcel.fiber_available === false && parcel.nearest_fiber_miles != null && Number(parcel.nearest_fiber_miles) > 5)
    risks.push({ description: `Fiber buildout required (${parcel.nearest_fiber_miles} mi)`, severity: 'LOW', mitigation: 'Standard fiber extension; contact carriers for LOI and timeline' });
  if (parcel.nearest_substation_miles != null && Number(parcel.nearest_substation_miles) > 25)
    risks.push({ description: `Remote from power grid (${parcel.nearest_substation_miles} mi)`, severity: 'MED', mitigation: 'Evaluate grid interconnect cost vs island mode operation' });
  if (!parcel.flood_zone)
    risks.push({ description: 'FEMA flood mapping gap - unmapped area', severity: 'LOW', mitigation: 'Commission site drainage assessment; flash flood risk from arroyos possible in unmapped rural areas' });

  // Gas interconnect: pipeline nearby but no existing tap — new construction needed.
  // Skip when any nearby pipeline is effectively on-site (parcel owner operates it,
  // or a WV DEP facility permit already names this parcel's capacity source).
  if (!gates.no_gas_path
      && pipelines && pipelines.length > 0
      && pipelines.some(p => Number(p.distance_miles) <= 5)
      && !pipelines.some(p => pipelineIsEffectivelyOnSite(parcel, p))) {
    risks.push({
      description: 'New pipeline interconnect required — no existing tap/meter station',
      severity: 'LOW',
      mitigation: lateralEstimatesSuppressed(parcel)
        ? 'Lateral routing, cost and schedule come from the pipeline operator\u2019s interconnect study; not estimated here.'
        : 'Lateral construction typical: 6–18 months, $1–3M/mile. Contact pipeline operator for interconnect study.',
    });
  }

  if (parcel.wetland_acres != null && Number(parcel.wetland_acres) > 0) {
    const severity = Number(parcel.wetland_pct) >= 20 ? 'HIGH' as const : Number(parcel.wetland_acres) > 5 ? 'MED' as const : 'LOW' as const;
    risks.push({ description: `Wetlands present (${Number(parcel.wetland_acres).toFixed(1)} ac, ${Number(parcel.wetland_pct).toFixed(0)}% — ${parcel.wetland_types || 'type TBD'})`, severity, mitigation: 'Section 404 applicability is determined after delineation and USACE review. Engage USACE early; formal wetland delineation ($15–50K) precedes construction planning.' });
  }
  if (parcel.wildfire_risk_score != null && Number(parcel.wildfire_risk_score) >= 3)
    risks.push({ description: `Elevated wildfire hazard (WHP ${Number(parcel.wildfire_risk_score).toFixed(1)}/5)`, severity: 'MED', mitigation: 'Defensible space and fire-resistant materials required; insurance premiums will reflect elevated risk' });

  if (parcel.water_sourcing_tier === 'nm_closure')
    risks.push({ description: 'NM closure area — no new groundwater appropriations', severity: 'HIGH', mitigation: 'Must purchase/lease existing water right or arrange trucked water. Contact NM OSE for transfer options.' });
  else if (!parcel.water_service_area && !parcel.aquifer_name && parcel.nearest_water_miles == null)
    risks.push({ description: 'Water access unknown', severity: 'MED', mitigation: 'Evaluate groundwater availability via TWDB well logs; assess trucked water economics for ~1,000 GPD auxiliary requirement (air-cooled, zero evaporative makeup)' });

  if (parcel.ej_max_pctile != null && parcel.ej_max_pctile >= 90) {
    risks.push({ description: `EJ concern: ${parcel.ej_max_pctile}th percentile (${parcel.ej_detail?.max_ej_indicator || 'EJ Index'})`, severity: 'HIGH', mitigation: 'Enhanced community engagement and environmental justice review required per EPA guidelines' });
  } else if (parcel.ej_max_pctile != null && parcel.ej_max_pctile >= 80) {
    risks.push({ description: `EJ warning: ${parcel.ej_max_pctile}th percentile (${parcel.ej_detail?.max_ej_indicator || 'EJ Index'})`, severity: 'MED', mitigation: 'Additional stakeholder engagement recommended during permitting' });
  }

  return risks;
}

// ── Resolve Before LOI items ──
export interface ResolveItem {
  what: string;
  why: string;
  cost: string;
  timeline: string;
}

export function computeResolveItems(
  parcel: ParcelDetail,
  pipelines: NearbyPipeline[] | null,
  featured: NearbyPipeline | null,
  tduName?: string | null,
  // Whether a LIVE operator supply zone backs this briefing. Callers that know
  // (BriefingContent passes `!!data.gasSupplyZone`) should pass it; omitting it
  // keeps the legacy parcels.gas_supply_zone_slug behavior for every other caller.
  inOperatorAoi?: boolean,
  // Crown Pine A1. Suppresses the lateral-construction item entirely: the
  // acquisition premises were extended to the pipeline, so the line is reached
  // by the land itself and a pre-terms lateral-construction gate no longer
  // describes the deal. (The ET facilities scope is still surfaced — it is a
  // kill-criteria row on the memo, not a land gate.) Every other caller is
  // untouched.
  isA1?: boolean,
): ResolveItem[] {
  const items: ResolveItem[] = [];

  // 1. Gas bridge — if featured pipeline is DSW (under construction)
  if (featured && isDesertSW(featured.name || '')) {
    const bridge = pipelines?.find(p => !isDesertSW(p.name || '') && Number(p.distance_miles) <= 5 && (pipelineCapacityDisplay(p).capacityMmcf ?? 0) > 0);
    const bridgeName = bridge ? (bridge.name || bridge.operator || 'nearby pipeline') : 'alternate pipeline';
    const bridgeDist = bridge?.distance_miles ?? '< 5';
    items.push({
      what: 'Interim gas supply during DSW construction',
      why: `Desert Southwest pipeline in-service 2027. ${bridgeName} at ${bridgeDist} mi is the bridge \u2014 confirm available capacity and interconnect logistics support 160 MW Phase 1 launch before DSW commissioning.`,
      cost: '$1\u20133M',
      timeline: 'Tap + meter station',
    });
  }

  // 2. Water — only if truly unresolved. On-site well potential is resolved (nearby wells confirm feasibility).
  const effectiveWt = computeEffectiveWaterTier(parcel);
  const waterDisclosure = (parcel as any).structured_intel?.landowner_disclosures?.water_on_site;
  if (waterDisclosure?.display_note) {
    items.push({
      what: 'Confirm landowner-reported water specifications',
      why: 'Landowner-verified water on site. Request source documentation (well log, operator records, or existing-infrastructure drawings) from landowner / compressor station operator and confirm with a site visit — pending source type (well / spring / pond / tap), exact on-parcel location, yield (GPM), depth, and quality measurements.',
      cost: 'Operator materials + site visit',
      timeline: '1–2 weeks',
    });
  } else if (effectiveWt === 'nm_closure') {
    items.push({
      what: 'Water rights acquisition',
      why: 'NM closure area \u2014 no new groundwater appropriations. Must purchase or lease existing water right.',
      cost: '$50\u2013200K',
      timeline: 'OSE transfer: 3\u201312 months',
    });
  } else if (!effectiveWt || effectiveWt === 'trucked') {
    // Paces hardcode bypass \u2014 at ~1,000 GPD BTM auxiliary the verification
    // step is subsumed by the briefing's water-as-de-minimis framing in the
    // Water Sourcing section below. Other parcels still get the item.
    // Cooper Draw rep parcel (Crockett account 6253; its UUID differs locally and in prod):
    // the SDR well set behind the tier excludes Crockett and Val Verde, but TWDB's
    // groundwater database holds ten historical wells inside the ranch.
    if (parcel.state === 'TX' && parcel.county === 'Crockett' && parcel.parcel_id_local === '6253') {
      items.push({
        what: 'Water sourcing verification',
        why: 'BTM air-cooled auxiliary needs ~1,000 GPD (zero evaporative makeup). TWDB records ten historical wells inside the ranch (282–550 ft) with no yield data, and the listing cites seven wells. Pump-test the listed wells and sample water quality.',
        cost: '$15–30K',
        timeline: 'Pump tests',
      });
    } else if (parcel.id !== 'a9ec56ff-f6dc-490d-9ae0-fabfa790081f') {
      items.push({
        what: 'Water sourcing verification',
        why: `BTM air-cooled auxiliary needs ~1,000 GPD (zero evaporative makeup). No productive wells within 2 mi. Evaluate commercial hauling economics or commission hydrogeological survey.`,
        cost: '$15\u201330K',
        timeline: 'Hydrogeological survey',
      });
    }
  }
  // If wt is municipal, aquifer_*, or on_site_well — water is resolved, no item.

  // 3. Dark fiber — only if no pipeline fiber leads AND no carrier backbone nearby
  const hasPipelineFiber = parcel.pipeline_fiber_operators && parcel.pipeline_fiber_operators.length > 0;
  const hasBackbone = parcel.nearest_carrier_fiber_miles != null && Number(parcel.nearest_carrier_fiber_miles) <= 20;
  if (!hasPipelineFiber && !hasBackbone) {
    const ixpNote = parcel.nearest_ixp_name ? ` to ${parcel.nearest_ixp_name}` : '';
    const providerNote = parcel.nearest_fiber_miles != null && Number(parcel.nearest_fiber_miles) <= 10
      ? `Nearest provider at ${parcel.nearest_fiber_miles} mi. `
      : '';
    items.push({
      what: 'Dark fiber pathway & cost',
      why: `${providerNote}AI training workloads are latency-insensitive \u2014 confirm dark fiber buildout cost${ixpNote}.`,
      cost: 'Carrier inquiry',
      timeline: '2\u20134 weeks',
    });
  }

  // 4. Lateral construction — if featured pipeline is far AND the parcel
  //    is NOT in an operator-zone AOI. Inside an AOI (e.g. revenant-east-tx),
  //    the firm-path supply is the operator's gathering build, not a public-
  //    midstream lateral; surfacing a 6–18mo / $X.XM lateral as Resolve-Before-
  //    LOI misframes the deal — a public-midstream lateral inside an operator
  //    zone is for expansion past the commitment or as redundancy, neither of
  //    which is a pre-LOI gate.
  const isInOperatorAoi = inOperatorAoi ?? !!parcel.gas_supply_zone_slug;
  // The featured pipeline is the headline, not necessarily the closest: on Paces the
  // featured line is TGP 100 at 3.07 mi while two other lines cross the parcel at 0 mi.
  // Testing `featured` alone put a $3.1–6.1M lateral on a page that also says gas
  // crosses the property. Gate on ANY line being effectively on site.
  const anyPipelineOnSite = (pipelines || []).some(p => pipelineIsEffectivelyOnSite(parcel, p));
  // A1: suppressed, not reworded. The acquisition premises were extended to the
  // pipeline, so the land itself reaches the line and there is no lateral to
  // build before terms. The ET facilities scope still surfaces as a memo
  // kill-criteria row — that is an operator scope, not a land gate.
  if (!isInOperatorAoi
      && !isA1
      && !anyPipelineOnSite
      && featured
      && featured.distance_miles != null
      && Number(featured.distance_miles) >= 0.5
      && !pipelineIsEffectivelyOnSite(parcel, featured)) {
    const dist = Number(featured.distance_miles);
    const lowCost = (dist * 1.0).toFixed(1);
    const highCost = (dist * 2.0).toFixed(1);
    const suppressed = lateralEstimatesSuppressed(parcel);
    items.push({
      what: 'Pipeline lateral construction',
      why: `${dist} miles from ${featured.name || featured.operator || 'pipeline'} to site boundary. Lateral routing survey and easement negotiation needed.`,
      cost: suppressed ? 'Not estimated' : `$${lowCost}\u2013${highCost}M`,
      timeline: suppressed ? 'Per operator interconnect study' : '6\u201318 months',
    });
  }

  return items;
}

// ── AML (Abandoned Mine Lands) overlap classification ──
// Tiers a single WVDEP AML overlap by reclamation-priority signal. The raw WVDEP
// columns encode funding and federal-inventory status separately; collapsing them
// into a single `aml_severity` summary loses the mitigation signal. Priority tiers
// (FUNDED, ACTIVE) carry regulatory-timeline risk; INVENTORIED is cataloged but
// dormant (Not in AMLIS and unfunded); COMPLETED is already reclaimed.
export type AmlTier = 'FUNDED' | 'ACTIVE' | 'INVENTORIED' | 'COMPLETED';

export function classifyAmlOverlap(o: AmlOverlap): AmlTier {
  if (o.severity === 'completed') return 'COMPLETED';
  if (o.status_raw && o.status_raw !== 'U' && o.status_raw !== '') return 'FUNDED';
  if ((o.severity === 'emergency' || o.severity === 'active') && o.amlis_status === 'In AMLIS') return 'ACTIVE';
  return 'INVENTORIED';
}

export function isPriorityAmlOverlap(o: AmlOverlap): boolean {
  const t = classifyAmlOverlap(o);
  return t === 'FUNDED' || t === 'ACTIVE';
}

// ── Effective water sourcing classification ──
// Refines the raw tier with well-proximity data: if the raw tier says
// "trucked" but there are productive wells nearby, the effective tier is
// "on-site well potential" — nearby wells prove groundwater is accessible.
export type EffectiveWaterTier =
  | 'municipal'
  | 'aquifer_no_permit'
  | 'aquifer_gcd'
  | 'aquifer_az_free'
  | 'aquifer_ama'
  | 'aquifer_nm'
  | 'nm_closure'
  | 'on_site_well'   // NEW: trucked + wells nearby
  | 'trucked'        // Only when no wells nearby
  | null;

export function computeEffectiveWaterTier(parcel: ParcelDetail): EffectiveWaterTier {
  const raw = parcel.water_sourcing_tier as EffectiveWaterTier;
  if (!raw) return null;
  if (raw !== 'trucked') return raw;
  // Raw is trucked — check for nearby wells
  const wells = parcel.nearby_water_wells || [];
  const within2 = wells.filter((w: any) => Number(w.distance_miles) <= 2);
  if (within2.length > 0) return 'on_site_well';
  // Also check TWDB proximity field
  if (parcel.nearest_twdb_well_miles != null && Number(parcel.nearest_twdb_well_miles) <= 2) return 'on_site_well';
  return 'trucked';
}

// Short label for badges/dimension bars
export function waterTierLabel(tier: EffectiveWaterTier): string {
  switch (tier) {
    case 'municipal': return 'Municipal';
    case 'aquifer_no_permit': return 'Rule of capture';
    case 'aquifer_gcd': return 'GCD permit';
    case 'aquifer_az_free': return 'Unregulated';
    case 'aquifer_ama': return 'ADWR permit';
    case 'aquifer_nm': return 'OSE permit';
    case 'nm_closure': return 'Closure area';
    case 'on_site_well': return 'On-site well';
    case 'trucked': return 'Trucked';
    default: return '\u2014';
  }
}

export function buildConfidenceItems(
  parcel: ParcelDetail,
  pipelines: NearbyPipeline[] | null,
  carveOut?: { slug?: string | null; parcelIds: string[]; totalAcres: number | null; floodZonePct100yr: number | null } | null,
): ConfidenceItem[] {
  const items: ConfidenceItem[] = [];
  const featured = findFeaturedPipeline(parcel, pipelines, carveOut?.slug);

  if (featured) {
    const plLabel = featured.name || featured.operator || 'Primary pipeline';
    const plSource = parcel.state === 'TX' ? 'TX RRC / EIA' : 'EIA / PHMSA';
    const onSite = pipelineIsEffectivelyOnSite(parcel, featured);
    const ownedByParcelOwner = ownerOwnsPipeline(parcel.owner_name, featured);
    const permitSrc = parcel.pipeline_available_local_source;
    const plOnSiteSrc = ownedByParcelOwner
      ? `Parcel owner operates this pipeline — tap grantable on-parcel`
      : permitSrc && permitSrc.startsWith('wvdep_permit')
        ? `WV DEP ${permitSrc.replace('wvdep_permit_', '')} facility permit`
        : `${plLabel} — ${plSource}`;
    items.push({
      field: 'Pipeline distance',
      value: onSite ? 'On site' : `${featured.distance_miles} mi`,
      confidence: 'confirmed',
      source: onSite ? plOnSiteSrc : `${plLabel} — ${plSource}`,
    });
    if (featured.maop_psig) items.push({ field: 'Pipeline MAOP', value: `${Number(featured.maop_psig).toLocaleString()} psig`, confidence: 'confirmed', source: featured.maop_source || 'FERC Form 2' });
    else items.push({ field: 'Pipeline MAOP', value: '\u2014', confidence: 'pending', source: 'Operator confirmation needed' });
    if (featured.diameter_inches) items.push({ field: 'Pipeline diameter', value: `${featured.diameter_inches}\u2033`, confidence: 'confirmed', source: plSource });

    items.push({ field: 'Available capacity', value: 'Unverified', confidence: 'pending', source: 'Operator confirmation of site-specific supply, operating pressure and firm delivery is outstanding' });
  } else {
    items.push({ field: 'Pipeline access', value: '\u2014', confidence: 'pending', source: 'No pipelines within 10 mi' });
  }

  if (parcel.nearest_gas_plant_miles != null) {
    const gpSource = parcel.state === 'TX'
      ? 'RRC R-3 / EIA-757 \u2014 NGL extraction / processing plants (not compressor stations)'
      : 'EIA-757 (2017 survey) \u2014 NGL extraction / processing plants (not compressor stations)';
    items.push({ field: 'Nearest gas processing plant', value: `${parcel.nearest_gas_plant_miles} mi`, confidence: 'preliminary', source: gpSource });
  } else {
    items.push({ field: 'Nearest gas processing plant', value: '\u2014', confidence: 'pending', source: 'Data pending' });
  }

  if (parcel.flood_zone) {
    const high = isHighRiskFloodZone(parcel.flood_zone);
    // Prefer carve-out pct when the parcel is a member — matches floodSeverity()
    // order, so Paces shows 8% (carve-out) rather than 22% (rep parcel alone).
    const inCarveOut = !!(carveOut && carveOut.parcelIds && carveOut.parcelIds.includes(parcel.id));
    const carveOutPct = inCarveOut ? carveOut!.floodZonePct100yr : null;
    const parcelPct = parcel.flood_zone_pct_100yr != null ? Number(parcel.flood_zone_pct_100yr) : null;
    const pct = carveOutPct != null ? Number(carveOutPct) : parcelPct;
    const scope = carveOutPct != null ? 'carve-out' : 'parcel';
    const pctNote = high && pct != null ? `, ${pct.toFixed(pct < 10 ? 1 : 0)}% of ${scope}` : '';
    items.push({
      field: 'Flood zone',
      value: `${parcel.flood_zone} (${high ? 'High Risk' : 'Minimal'}${pctNote})`,
      confidence: 'confirmed',
      source: 'FEMA NFHL',
    });
  }
  if (parcel.wetland_acres != null) {
    items.push({ field: 'Wetlands (NWI)', value: Number(parcel.wetland_acres) > 0 ? `${Number(parcel.wetland_acres).toFixed(1)} ac (${Number(parcel.wetland_pct).toFixed(0)}%)` : 'None mapped', confidence: 'preliminary', source: `USFWS NWI${parcel.wetland_types ? ` — ${parcel.wetland_types}` : ''}` });
  }
  if (parcel.wildfire_risk_score != null) {
    items.push({ field: 'Wildfire hazard', value: `WHP ${Number(parcel.wildfire_risk_score).toFixed(1)}/5 (${Number(parcel.wildfire_risk_score) < 1 ? 'very low' : Number(parcel.wildfire_risk_score) < 2 ? 'low' : Number(parcel.wildfire_risk_score) < 3 ? 'moderate' : 'elevated'})`, confidence: 'confirmed', source: 'USFS Wildfire Hazard Potential' });
  }
  items.push({
    field: 'Avg slope',
    value: parcel.avg_slope_pct != null ? `${parcel.avg_slope_pct}%` : 'Unavailable',
    confidence: parcel.avg_slope_pct != null ? 'confirmed' : 'pending',
    source: parcel.avg_slope_pct != null ? 'USGS DEM' : 'Outside DEM coverage',
  });

  if (parcel.in_nonattainment != null) {
    items.push({ field: 'EPA attainment', value: parcel.in_nonattainment ? 'Nonattainment' : 'Attainment', confidence: 'confirmed', source: 'EPA Green Book' });
  } else {
    items.push({ field: 'EPA attainment', value: '\u2014', confidence: 'pending', source: 'EPA data not loaded' });
  }

  // EJ Screen
  if (parcel.ej_max_pctile != null) {
    const indicator = parcel.ej_detail?.max_ej_indicator || 'EJ Index';
    items.push({
      field: 'EJ Screen Index',
      value: parcel.ej_max_pctile >= 80
        ? `${parcel.ej_max_pctile}th pctile (${indicator})`
        : `Clear (${parcel.ej_max_pctile}th pctile)`,
      confidence: 'confirmed',
      source: `EPA EJScreen v2.3, BG ${parcel.ej_block_group_geoid}`,
    });
  } else {
    items.push({ field: 'EJ Screen Index', value: '\u2014', confidence: 'pending', source: 'EJScreen data not loaded' });
  }

  if (parcel.nearest_substation_miles != null) {
    items.push({ field: 'Nearest substation', value: `${parcel.nearest_substation_miles} mi${parcel.nearest_substation_kv ? ` (${Number(parcel.nearest_substation_kv).toLocaleString()} kV)` : ''}`, confidence: 'confirmed', source: 'HIFLD' });
    items.push({ field: 'Substation hosting capacity', value: '\u2014', confidence: 'pending', source: 'Utility inquiry needed: hosting capacity (MW), queue position, gen-tie cost' });
  }
  items.push({ field: 'Fiber coverage', value: parcel.fiber_available == null ? '\u2014' : parcel.fiber_available ? 'Available' : 'Not available', confidence: parcel.fiber_available != null ? 'preliminary' : 'pending', source: parcel.fiber_available != null ? 'FCC BDC (self-reported)' : 'No data' });
  if (parcel.nearest_fiber_miles != null) {
    items.push({ field: 'Fiber type & latency', value: '\u2014', confidence: 'pending', source: 'Carrier confirmation needed: lit/dark fiber, route diversity, IX latency' });
  }
  if (parcel.water_sourcing_tier === 'municipal') {
    items.push({ field: 'Water access', value: 'Municipal service area', confidence: 'confirmed', source: 'TWDB' });
  } else if (parcel.water_sourcing_tier === 'aquifer_no_permit') {
    items.push({ field: 'Water access', value: parcel.state === 'TX' ? `${formatAquiferName(parcel.aquifer_name)} aquifer — rule of capture (no groundwater permit; TDLR driller required)` : `${formatAquiferName(parcel.aquifer_name)} aquifer — confirm state groundwater permit requirements`, confidence: 'confirmed', source: parcel.state === 'TX' ? 'TWDB + GCD boundaries' : 'USGS NWIS + state regulator' });
  } else if (parcel.water_sourcing_tier === 'aquifer_gcd') {
    items.push({ field: 'Water access', value: `${formatAquiferName(parcel.aquifer_name)} aquifer — ${parcel.water_regulatory_zone || 'GCD'} permit required`, confidence: 'confirmed', source: 'TWDB + GCD boundaries' });
  } else if (parcel.water_sourcing_tier === 'aquifer_az_free') {
    items.push({ field: 'Water access', value: `${formatAquiferName(parcel.aquifer_name)} aquifer — unregulated (outside AMA)`, confidence: 'confirmed', source: 'ADWR' });
  } else if (parcel.water_sourcing_tier === 'aquifer_ama') {
    items.push({ field: 'Water access', value: `${parcel.water_regulatory_zone || 'AMA'} — ADWR permit required`, confidence: 'confirmed', source: 'ADWR' });
  } else if (parcel.water_sourcing_tier === 'aquifer_nm') {
    items.push({ field: 'Water access', value: `${formatAquiferName(parcel.aquifer_name)} aquifer — OSE permit required`, confidence: 'confirmed', source: 'NM OSE' });
  } else if (parcel.water_sourcing_tier === 'nm_closure') {
    items.push({ field: 'Water access', value: 'Closure area — no new appropriations', confidence: 'confirmed', source: 'NM OSE' });
  } else if (parcel.water_sourcing_tier === 'trucked' || parcel.water_sourcing_tier === 'on_site_well') {
    const effective = computeEffectiveWaterTier(parcel);
    const zoneNote = parcel.water_regulatory_zone ? ` (${parcel.water_regulatory_zone})` : '';
    if (effective === 'on_site_well') {
      const wells = (parcel.nearby_water_wells || []).filter((w: any) => Number(w.distance_miles) <= 2);
      const wellCount = wells.length;
      items.push({ field: 'Water access', value: `On-site well potential${zoneNote} — ${wellCount} productive well${wellCount > 1 ? 's' : ''} within 2 mi`, confidence: 'confirmed', source: 'TWDB Submitted Drillers Reports' });
    } else {
      items.push({ field: 'Water access', value: `Trucked water${zoneNote} — no local source within 2 mi`, confidence: 'confirmed', source: 'TWDB + regulatory zones' });
    }
  } else if (parcel.water_service_area) {
    items.push({ field: 'Water access', value: 'Municipal service area', confidence: 'confirmed', source: 'TWDB' });
  } else if (parcel.aquifer_name) {
    items.push({ field: 'Water access', value: `Aquifer (${formatAquiferName(parcel.aquifer_name)})`, confidence: 'confirmed', source: 'TWDB' });
  } else {
    // Landowner disclosure overrides "No data" \u2014 surfaces pending specs rather than a blank.
    const ldWater = (parcel as any).structured_intel?.landowner_disclosures?.water_on_site;
    if (ldWater?.display_note) {
      const outstanding = Array.isArray(ldWater.outstanding_items) && ldWater.outstanding_items.length > 0
        ? ` Pending: ${ldWater.outstanding_items.join('; ')}.`
        : '';
      items.push({
        field: 'Water access',
        value: 'Landowner-verified on site (specs pending)',
        confidence: 'pending',
        source: `Landowner disclosure \u2014 operator materials + site visit.${outstanding}`,
      });
    } else {
      items.push({ field: 'Water access', value: '\u2014', confidence: 'pending', source: 'No data - evaluate via TWDB well logs' });
    }
  }

  const nearbyWells = parcel.nearby_water_wells || [];
  const within2 = nearbyWells.filter((w: any) => Number(w.distance_miles) <= 2);
  if (within2.length > 0) {
    const depths = within2.map((w: any) => w.total_depth_ft).filter(Boolean);
    const yields = within2.map((w: any) => w.yield_gpm).filter(Boolean);
    const parts = [`${within2.length} well${within2.length > 1 ? 's' : ''} within 2 mi`];
    if (depths.length > 0) parts.push(`avg ${Math.round(depths.reduce((a: number, b: any) => a + Number(b), 0) / depths.length)} ft`);
    if (yields.length > 0) parts.push(`avg ${Math.round(yields.reduce((a: number, b: any) => a + Number(b), 0) / yields.length)} GPM`);
    items.push({ field: 'Well feasibility', value: parts.join(', '), confidence: 'confirmed', source: 'TWDB Submitted Drillers Reports' });
  } else if (parcel.nearest_twdb_well_miles != null) {
    items.push({ field: 'Well feasibility', value: `Nearest TWDB well: ${parcel.nearest_twdb_well_miles} mi`, confidence: 'confirmed', source: 'TWDB SDR' });
  }

  // Per-tranche permit pathway — 160 MW phase with SCR controls
  if (parcel.in_nonattainment != null) {
    const trancheNox = Math.round(BTM_CALIBRATION.TRANCHE_MW * BTM_CALIBRATION.NOX_TPY_PER_MW_SCR);
    const state = parcel.state || '';

    if (parcel.in_nonattainment) {
      const nnsr = getNnsrDetails(parcel.nonattainment_areas);
      const cls = nnsr.classification ? ` (${nnsr.classification})` : '';
      items.push({
        field: 'Air permit pathway',
        value: `NNSR required — ${trancheNox} TPY NOx/phase exceeds ${nnsr.threshold} TPY${cls} threshold`,
        confidence: 'assumed',
        source: 'Inferred from nonattainment classification + 160 MW phase',
      });
    } else if (state === 'NM') {
      items.push({
        field: 'Air permit pathway',
        value: `PSD required — ${trancheNox} TPY NOx/phase exceeds 25 TPY NM threshold`,
        confidence: 'assumed',
        source: 'Inferred from NM minor source threshold + 160 MW phase',
      });
    } else if (state === 'AZ') {
      items.push({
        field: 'Air permit pathway',
        value: `PSD required — ${trancheNox} TPY NOx/phase exceeds 100 TPY AZ threshold`,
        confidence: 'assumed',
        source: 'Inferred from ADEQ major source threshold + 160 MW phase',
      });
    } else if (state === 'TX') {
      // TX attainment: TCEQ NG EGU Standard Permit is the no-SCR pathway
      // (0.61 TPY/MW), matching the phased permitting ladder — 160 MW → ~98 TPY
      // NOx, well under the 250 TPY major-source threshold.
      const noxSp = Math.round(BTM_CALIBRATION.TRANCHE_MW * BTM_CALIBRATION.NOX_TPY_PER_MW_SP);
      items.push({
        field: 'Air permit pathway',
        value: `Standard permit (${BTM_CALIBRATION.TRANCHE_MW} MW/phase, ~${noxSp} TPY NOx at the 0.14 lb/MWh limit < 250 TPY)`,
        confidence: 'assumed',
        source: 'Inferred — standard permit eligibility at phase scale; controls equipment-specific',
      });
    } else {
      // WV/other attainment: SCR-controlled pathway → 160 MW × 1.2 TPY/MW = 192 TPY < 250 TPY.
      items.push({
        field: 'Air permit pathway',
        value: `Standard permit (${BTM_CALIBRATION.TRANCHE_MW} MW/phase with SCR, ${trancheNox} TPY NOx < 250 TPY)`,
        confidence: 'assumed',
        source: 'Inferred — standard permit eligibility at phase scale',
      });
    }
  }

  if (featured?.system_type === 'transmission' || featured?.pressure_grade === 'high') {
    items.push({ field: 'Firm transport', value: 'Likely', confidence: 'assumed', source: 'Inferred from transmission-grade pipeline' });
  } else if (featured) {
    items.push({ field: 'Firm transport', value: 'Uncertain', confidence: 'assumed', source: 'Gathering/unknown pipeline type' });
  }

  return items;
}

